import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import axios from 'axios';
import SortableTopFieldItem from './SortableTopFieldItem';
import TopFieldConfigModal from './TopFieldConfigModal';
import './TopSearchFields.css';

const TopSearchFieldsDragDrop = ({ showToast, onFieldsChange, initialFields = [] }) => {
  const [searchFields, setSearchFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  
  // Config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configField, setConfigField] = useState(null);
  
  // Add a ref to track if we're currently saving to prevent race conditions
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(null);

  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || '/wp-json/hvnlynab/v1';
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
  const SETTINGS_ENDPOINT = `${API_BASE}/plugin-settings/search`;

  const AXIOS_CONFIG = {
    headers: {
      'X-WP-Nonce': API_NONCE,
      'Content-Type': 'application/json'
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Default top search fields
  const DEFAULT_TOP_FIELDS = [
    { 
      id: 'bedrooms', 
      title: __('Bedrooms', 'havenlytics'), 
      type: 'number', 
      enabled: true, 
      order: 1, 
      is_default: true,
      config: {
        placeholder: __('Any', 'havenlytics'),
        options: ['1', '2', '3', '4', '5'],
        min: 1,
        max: 10,
        usePresetValues: true
      }
    },
    { 
      id: 'bathrooms', 
      title: __('Bathrooms', 'havenlytics'), 
      type: 'number', 
      enabled: true, 
      order: 2, 
      is_default: true,
      config: {
        placeholder: __('Any', 'havenlytics'),
        options: ['1', '2', '3', '4', '5'],
        min: 1,
        max: 10,
        usePresetValues: true
      }
    },
    { 
      id: 'min_price', 
      title: __('Min Price', 'havenlytics'), 
      type: 'range', 
      enabled: true, 
      order: 3, 
      is_default: true,
      config: {
        placeholder: __('Any', 'havenlytics'),
        options: ['50000', '75000', '100000', '200000', '300000', '400000', '500000'],
        usePresetValues: true
      }
    },
    { 
      id: 'max_price', 
      title: __('Max Price', 'havenlytics'), 
      type: 'range', 
      enabled: true, 
      order: 4, 
      is_default: true,
      config: {
        placeholder: __('Any', 'havenlytics'),
        options: ['500000', '600000', '700000', '800000', '900000', '1000000'],
        usePresetValues: true
      }
    }
  ];

  useEffect(() => {
    if (initialFields && initialFields.length > 0) {
      const sortedFields = [...initialFields].sort((a, b) => (a.order || 0) - (b.order || 0));
      setSearchFields(sortedFields);
      setIsLoading(false);
    } else {
      loadTopSearchFields();
    }
  }, [initialFields]);

  const loadTopSearchFields = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
      
      if (response.data?.success && response.data?.data) {
        const searchData = response.data.data;
        let fields = searchData.hvnly_top_search_fields || [];
        
        // Convert object format to array if needed
        if (fields && !Array.isArray(fields)) {
          fields = Object.values(fields);
        }
        
        if (fields.length > 0) {
          fields.sort((a, b) => (a.order || 0) - (b.order || 0));
          setSearchFields(fields);
          if (onFieldsChange) {
            onFieldsChange(fields);
          }
        } else {
          setSearchFields([...DEFAULT_TOP_FIELDS]);
        }
      } else {
        setSearchFields([...DEFAULT_TOP_FIELDS]);
      }
    } catch (error) {
      console.error('Failed to load top search fields:', error);
      setSearchFields([...DEFAULT_TOP_FIELDS]);
      if (showToast) {
        showToast('warning', __('Notice', 'havenlytics'), __('Using default top search fields', 'havenlytics'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveTopSearchFields = async (fields) => {
    if (isSavingRef.current) {
      pendingSaveRef.current = fields;
      return;
    }
    
    isSavingRef.current = true;
    
    try {
      const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
      const currentSettings = response.data?.data || {};
      
      const updatedSettings = { ...currentSettings };
      updatedSettings.hvnly_top_search_fields = fields;
      
      const saveResponse = await axios.post(SETTINGS_ENDPOINT, updatedSettings, AXIOS_CONFIG);
      
      if (saveResponse.data?.success) {
        if (onFieldsChange) {
          onFieldsChange(fields);
        }
        
        if (pendingSaveRef.current) {
          const pending = pendingSaveRef.current;
          pendingSaveRef.current = null;
          setTimeout(() => saveTopSearchFields(pending), 100);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to save top search fields:', error);
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to save top search fields', 'havenlytics'));
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const moveFieldUp = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === 0) return prev;
      
      const newFields = [...prev];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      
      newFields.forEach((field, idx) => {
        field.order = idx + 1;
      });
      
      setTimeout(() => saveTopSearchFields(newFields), 0);
      return newFields;
    });
  };

  const moveFieldDown = (id) => {
    setSearchFields(prev => {
      const index = prev.findIndex(field => field.id === id);
      if (index === prev.length - 1) return prev;
      
      const newFields = [...prev];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      
      newFields.forEach((field, idx) => {
        field.order = idx + 1;
      });
      
      setTimeout(() => saveTopSearchFields(newFields), 0);
      return newFields;
    });
  };

  const toggleField = (id) => {
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === id ? { ...field, enabled: !field.enabled } : field
      );
      
      setTimeout(() => saveTopSearchFields(newFields), 0);
      return newFields;
    });
  };

  const startEditing = (field) => {
    setEditingField(field.id);
    setEditTitle(field.title);
  };

  const saveEdit = () => {
    if (!editTitle.trim()) {
      if (showToast) {
        showToast('error', __('Error', 'havenlytics'), __('Field title cannot be empty', 'havenlytics'));
      }
      return;
    }
    
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === editingField 
          ? { ...field, title: editTitle } 
          : field
      );
      
      setTimeout(() => saveTopSearchFields(newFields), 0);
      return newFields;
    });
    
    setEditingField(null);
    if (showToast) {
      showToast('success', __('Success', 'havenlytics'), __('Field title updated!', 'havenlytics'));
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
  };

  const handleConfigureField = (field) => {
    setConfigField(field);
    setConfigModalOpen(true);
  };

  const handleSaveFieldConfig = async (updatedField) => {
    setSearchFields(prev => {
      const newFields = prev.map(field => 
        field.id === updatedField.id ? updatedField : field
      );
      setTimeout(() => saveTopSearchFields(newFields), 0);
      return newFields;
    });
    showToast('success', __('Success', 'havenlytics'), __('Field configuration saved!', 'havenlytics'));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSearchFields((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        const newItems = [...items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);
        
        newItems.forEach((item, index) => {
          item.order = index + 1;
        });
        
        setTimeout(() => saveTopSearchFields(newItems), 0);
        return newItems;
      });
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'text': __('Text', 'havenlytics'),
      'number': __('Number', 'havenlytics'),
      'dropdown': __('Dropdown', 'havenlytics'),
      'checkbox': __('Checkbox', 'havenlytics'),
      'range': __('Range', 'havenlytics'),
      'group': __('Group', 'havenlytics'),
      'date': __('Date', 'havenlytics')
    };
    return types[type] || type;
  };

  if (isLoading) {
    return (
      <div className="hvnly_top_search_fields_drag_drop_builder">
        <div className="hvnly_top_search_fields_drag_drop_loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{__('Loading top search fields...', 'havenlytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hvnly_top_search_fields_drag_drop_builder">
      <div className="hvnly_top_search_fields_drag_drop_header">
        <i className="fas fa-list"></i>
        <span>{__('Manage Top Search Fields', 'havenlytics')}</span>
        <span className="hvnly_top_search_fields_drag_drop_hint">
          {__('Drag to reorder | Click Edit to rename | Click Configure to set options', 'havenlytics')}
        </span>
      </div>
      
      <div className="hvnly_top_search_fields_drag_drop_body">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={searchFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="hvnly_top_search_fields_drag_drop_list">
              {searchFields.length > 0 ? (
                searchFields.map((field, index) => (
                  <SortableTopFieldItem
                    key={field.id}
                    field={{...field, typeLabel: getTypeLabel(field.type)}}
                    index={index}
                    onMoveUp={moveFieldUp}
                    onMoveDown={moveFieldDown}
                    onToggle={toggleField}
                    onEdit={startEditing}
                    onConfigure={handleConfigureField}
                    isEditing={editingField === field.id}
                    editTitle={editTitle}
                    onEditTitleChange={setEditTitle}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    fieldsLength={searchFields.length}
                    isDefault={field.is_default}
                  />
                ))
              ) : (
                <p style={{ textAlign: 'center', padding: '40px' }}>
                  {__('No top search fields found.', 'havenlytics')}
                </p>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <TopFieldConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setConfigField(null);
        }}
        field={configField}
        onSave={handleSaveFieldConfig}
        showToast={showToast}
      />
    </div>
  );
};

export default TopSearchFieldsDragDrop;